import React from "react";
import history from "./Routes/history";
import { URL } from "./api/tklEnvironments";

/**
 * Collect URL + user for error reports.
 * Store is required lazily: App.js imports this module, so a top-level
 * `import { store } from "./App"` would cycle and can blank the tree.
 */
function getReportingContext() {
  const location =
    typeof window !== "undefined" && window.location
      ? window.location
      : null;

  const url = location?.href || null;
  const pathname =
    location?.pathname || history?.location?.pathname || null;
  const search = location?.search || history?.location?.search || null;
  const hash = location?.hash || history?.location?.hash || null;

  const hasToken =
    typeof localStorage !== "undefined" && !!localStorage.getItem("token");
  const roleFromStorage =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("userRole")
      : null;

  let user = null;
  try {
    // eslint-disable-next-line global-require
    const { store } = require("./App");
    const userData = store?.getState?.()?.users?.userData;
    if (userData && (userData.userId || userData.email || userData.userSlug)) {
      user = {
        id: userData.userId ?? null,
        slug: userData.userSlug ?? null,
        email: userData.email || null,
        name:
          [userData.firstName, userData.lastName].filter(Boolean).join(" ") ||
          null,
        roles: Array.isArray(userData.roles) ? userData.roles : null,
        anonymous: false,
        hasToken,
        roleFromStorage,
      };
    }
  } catch (e) {
    // ignore store access failures
  }

  if (!user) {
    user = {
      id: null,
      slug: null,
      email: null,
      name: null,
      roles: null,
      anonymous: true,
      hasToken,
      roleFromStorage,
    };
  }

  return { url, pathname, search, hash, user };
}

/** Always persist to DB — anonymous when user cannot be identified. */
function persistClientError(error, errorInfo, context) {
  try {
    if (!URL) {
      return;
    }

    const token =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("token")
        : null;
    const { user } = context;
    const anonymous = !user?.id && !user?.email && !user?.slug;

    const body = {
      url: context.url,
      pathname: context.pathname,
      search: context.search,
      hash: context.hash,
      error_name: error?.name || null,
      error_message: error?.message || String(error),
      error_stack: error?.stack || null,
      component_stack: errorInfo?.componentStack || null,
      user_email: anonymous ? "anonymous" : user.email || null,
      user_name: anonymous ? "anonymous" : user.name || null,
      user_slug: anonymous ? "anonymous" : user.slug || null,
      metadata: {
        userId: user?.id ?? null,
        roles: user?.roles ?? null,
        anonymous,
        hasToken: !!token,
        roleFromStorage: user?.roleFromStorage ?? null,
      },
    };

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Fire-and-forget; never let logging fail the boundary UI.
    fetch(`${URL}/log-client-error`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    // ignore reporting failures
  }
}

export default class TKLErrorBoundary extends React.Component {
  constructor(props) {
    super(props);

    this.state = { error: null, errorInfo: null, hasError: false };
    this.unlisten = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidMount() {
    this.unlisten = history.listen(() => {
      if (this.state.hasError) {
        this.setState({ hasError: false, error: null, errorInfo: null });
      }
    });
  }

  componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten();
    }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    const context = getReportingContext();
    // Always log so "Something went wrong" reports can be diagnosed from the console.
    console.error("TKLErrorBoundary caught:", error, errorInfo, context);

    persistClientError(error, errorInfo, context);

    // PL-24: report to Rollbar when available (prod snippet), with URL + user.
    try {
      if (typeof window !== "undefined" && window.Rollbar) {
        const { user } = context;
        const personId = user?.id ?? user?.slug ?? user?.email;
        if (personId && typeof window.Rollbar.configure === "function") {
          window.Rollbar.configure({
            payload: {
              person: {
                id: String(personId),
                email: user.email || undefined,
                username: user.name || undefined,
              },
            },
          });
        }
        window.Rollbar.error(error, {
          reactErrorInfo: errorInfo,
          ...context,
        });
      }
    } catch (e) {
      // ignore reporting failures
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="pg-main pb-3 pb-md-5">
          <div className="container py-5 text-center">
            <h1 className="h3">Something went wrong</h1>
            <p className="text-muted mb-3">
              Please refresh the page. If the problem continues, contact support.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>
          </div>
        </main>
      );
    }

    return this.props.children;
  }
}
